/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import { createRouter, createWebHistory } from 'vue-router/auto'
import { setupLayouts } from 'virtual:generated-layouts'
import { handleHotUpdate, routes } from 'vue-router/auto-routes'
import { setupGuards } from '@/router/guards.ts'
import { routes as extraRoutes, redirects } from '@/router/additional-routes.ts'
import type { RouteRecordRaw } from 'vue-router'

// Workaround
function recursiveLayouts (route: RouteRecordRaw) {
  if (route.children) {
    for (let i = 0; i < route.children.length; i++)
      route.children[i] = recursiveLayouts(route.children[i])

    return route
  }

  return setupLayouts([route])[0]
}


const router = createRouter({
  history: createWebHistory(window.BASE_URL),
  routes: routes.map(recursiveLayouts),
  scrollBehavior (to) {
    if (to.hash)
      return { el: to.hash, behavior: 'smooth', top: 60 }

    return { top: 0 }
  },
})

setupGuards(router)

const addExtraRoutes = () => {
  for (const route of extraRoutes) {
    router.addRoute(route)
  }

  for (const redirect of redirects) {
    router.addRoute(redirect)
  }
}

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('Reloading page to fix dynamic import error')
      localStorage.setItem('vuetify:dynamic-reload', 'true')
      location.assign(to.fullPath)
    } else {
      console.error('Dynamic import error, reloading page did not fix it', err)
    }
  } else {
    console.error(err)
  }
})

router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload')
})

if (import.meta.hot) {
  handleHotUpdate(router, newRoutes => {
    setupGuards(router)

    addExtraRoutes()

    console.log('newRoutes', newRoutes)
    return newRoutes.map(recursiveLayouts)
  })
} else {
  // production
  addExtraRoutes()
}

export default router
